import { useMediaQuery } from '@mui/material';

export enum DeviceType {
	Mobile = 'mobile',
	Tablet = 'tablet',
	Desktop = 'desktop'
}

export const MOBILE_MAX_WIDTH = 767;
export const TABLET_MIN_WIDTH = 768;

const MOBILE_WIDTH = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
const TABLET_WIDTH = `(min-width: ${TABLET_MIN_WIDTH}px)`;
const TABLET_POINTER = '(pointer: coarse)';

/**
 * Determines the current device type based on media queries.
 *
 * Device type rules:
 * - Mobile: max-width 767px
 * - Tablet: min-width 768px, must have coarse pointer
 * - Desktop: anything else
 *
 * @returns DeviceType.mobile | DeviceType.tablet | DeviceType.desktop
 */
export const useDeviceType = () => {
	const isMobileWidth = useMediaQuery(MOBILE_WIDTH);
	// No max width for tablet, as they can get rather large sometimes. Not sure if we're going to cater to desktops with touch screens?
	const isTabletWidth = useMediaQuery(TABLET_WIDTH);
	const hasCoarsePointer = useMediaQuery(TABLET_POINTER);

	if (isMobileWidth) return DeviceType.Mobile;
	if (isTabletWidth && hasCoarsePointer) return DeviceType.Tablet;
	return DeviceType.Desktop;
};

/**
 * Hook that determines if the current device is a tablet.
 *
 * @returns True if the device has a min-width of 768px and coarse pointer (touch), false otherwise
 */
export const useIsTablet = () => {
	const isTabletWidth = useMediaQuery(TABLET_WIDTH);
	const hasCoarsePointer = useMediaQuery(TABLET_POINTER);

	return isTabletWidth && hasCoarsePointer;
};

/**
 * Hook that determines if the current device is mobile.
 *
 * @returns True if the device has a max-width of 767px, false otherwise
 */
export const useIsMobile = () => {
	return useMediaQuery(MOBILE_WIDTH);
};

/**
 * Hook that determines if the current device is mobile or tablet.
 *
 * This hook combines mobile and tablet devices into a single "mobile" category,
 * which is useful for responsive design where you want to treat both touch-based
 * devices similarly (e.g., showing mobile layouts, touch-friendly interactions).
 *
 * @returns True if the device is mobile (max-width 767px) or tablet (min-width 768px with coarse pointer), false for desktop
 */
export const useIsTabletOrMobile = () => {
	const isTablet = useIsTablet();
	const isMobile = useIsMobile();

	return isTablet || isMobile;
};
